/**
 * Ribbon command execution — the single dispatch point for every ribbon,
 * QAT and menu-bar binding (Faz 3). Canonical commands route through the
 * shared draft executor (the same path as typing in the command line, so
 * legacy-parser system commands like ZOOM behave identically); shell
 * actions drive the document/clipboard surfaces directly.
 */

import { executeDraftCommand } from '../../../core/dynamicInput/dynamicInputEngine';
import { CANONICAL_BY_ID } from '../../../core/commands/canonicalCommands';
import { useAppStore } from '../../../state/store';
import { useCommandManager } from '../../../state/commandStore';
import type { RibbonBinding, ShellActionId } from '../../../config/ribbonTabs';

/** First (primary) command-line spelling of a canonical command. */
export function primaryCommandName(cmdId: string): string {
    const cmd = CANONICAL_BY_ID.get(cmdId);
    return cmd?.names['2d'][0] ?? cmd?.names['3d'][0] ?? cmdId.toUpperCase();
}

/** The viewport tool a binding activates, if any (active-state lookup). */
export function bindingTool(binding: RibbonBinding): string | null {
    if (binding.tool) return binding.tool;
    if (binding.cmdId) {
        const cmd = CANONICAL_BY_ID.get(binding.cmdId);
        return cmd?.kind === 'activateTool' ? String(cmd.tool) : null;
    }
    return null;
}

function runShellAction(action: ShellActionId): void {
    const execute = useCommandManager.getState().executeCommand;
    switch (action) {
        case 'doc.new': execute('newProject'); break;
        case 'doc.open': execute('openProject'); break;
        case 'doc.save': execute('saveProject'); break;
        case 'doc.saveAs': execute('saveProjectAs'); break;
        case 'doc.saveAsTemplate': execute('saveAsTemplate'); break;
        case 'edit.cut': execute('cut'); break;
        case 'edit.copy': execute('copy'); break;
        case 'edit.paste': execute('paste'); break;
        case 'app.undo': useAppStore.getState().undo(); break;
        case 'app.redo': useAppStore.getState().redo(); break;
    }
}

/** Run one ribbon binding. Exactly one of the three fields drives it. */
export function runRibbonBinding(binding: RibbonBinding): void {
    if (binding.shell) {
        runShellAction(binding.shell);
        return;
    }
    if (binding.tool) {
        useAppStore.getState().setActiveTool(binding.tool as never);
        return;
    }
    if (binding.cmdId) {
        executeDraftCommand(primaryCommandName(binding.cmdId));
    }
}

/** "Command: NAME" tail for tooltips (OCS tooltip composition). */
export function commandHint(binding: RibbonBinding): string | null {
    if (binding.cmdId) return `Command: ${primaryCommandName(binding.cmdId)}`;
    if (binding.shell) return null;
    if (binding.tool) return `Tool: ${binding.tool}`;
    return null;
}
